/**
 * Puck Configuration for Amplifier MVP
 * 
 * This config defines all available components for the page builder.
 * Used by both the dashboard (editor) and store (render).
 */

import type { Config, Data } from '@measured/puck'

import {
  HeroConfig,
  HeadingConfig,
  TextConfig,
  ImageConfig,
  VideoConfig,
  ButtonConfig,
  ColumnsConfig,
  ContainerConfig,
  SpacerConfig,
  WhatsAppButtonConfig,
  TestimonialConfig,
  FeaturesGridConfig,
  StoreInfoConfig,
} from './components'

/**
 * Puck configuration with all UMKM-focused components
 */
export const puckConfig: Config = {
  // Root configuration for page-level settings
  root: {
    fields: {
      title: {
        type: 'text',
        label: 'Judul Halaman',
      },
    },
    defaultProps: {
      title: '',
    },
    render: ({ children }: { children: React.ReactNode }) => {
      return <>{children}</>
    },
  },
  
  // All available components
  components: {
    // Layout components
    Container: ContainerConfig,
    Columns: ColumnsConfig,
    Spacer: SpacerConfig,
    
    // Content components
    Hero: HeroConfig,
    Heading: HeadingConfig,
    Text: TextConfig,
    Image: ImageConfig,
    Video: VideoConfig,
    Button: ButtonConfig,
    
    // UMKM-specific components
    WhatsAppButton: WhatsAppButtonConfig,
    Testimonial: TestimonialConfig,
    FeaturesGrid: FeaturesGridConfig,
    StoreInfo: StoreInfoConfig,
  },
  
  // Category organization for the component picker
  categories: {
    layout: {
      title: 'Layout',
      components: ['Container', 'Columns', 'Spacer'],
    },
    content: {
      title: 'Konten',
      components: ['Hero', 'Heading', 'Text', 'Image', 'Video', 'Button'],
    },
    umkm: {
      title: 'UMKM',
      components: ['WhatsAppButton', 'Testimonial', 'FeaturesGrid', 'StoreInfo'],
    },
  },
}

/**
 * Type for Puck data
 */
export type PuckData = Data

/**
 * Default page data for new pages
 */
export const defaultPageData: Data = {
  root: {
    props: {
      title: '',
    },
  },
  content: [],
  zones: {},
}

/**
 * Check if data is valid Puck format
 */
export function isPuckFormat(data: unknown): data is Data {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  return 'content' in d && 'root' in d && Array.isArray(d.content)
}
